import { JIKE_WEB_HOST } from '@/utils/request/host'
import { PostRouteType } from '@/utils/route'
import { z } from 'zod'
import { PUBLIC_POST_SOURCE_FIELDS_SCHEMA, PUBLIC_POST_VISIBILITY_SCHEMA } from './getPostContract'

// Public fields shared by the repost and its single-level reference card.
const PUBLIC_REFERENCE_FIELDS_SCHEMA = PUBLIC_POST_SOURCE_FIELDS_SCHEMA.extend({
	type: z.enum(['ORIGINAL_POST', 'REPOST']),
})

const PUBLIC_REFERENCE_SOURCE_SCHEMA = PUBLIC_REFERENCE_FIELDS_SCHEMA.extend(
	PUBLIC_POST_VISIBILITY_SCHEMA.shape
).transform(
	({
		status: _status,
		deleted: _deleted,
		isPrivate: _isPrivate,
		isSuppressed: _isSuppressed,
		...snapshot
	}) => ({
		...snapshot,
		link: `${JIKE_WEB_HOST}/u/${encodeURIComponent(snapshot.user.username)}/${snapshot.type === 'REPOST' ? PostRouteType.REPOST : PostRouteType.POST}/${snapshot.id}`,
	})
)

// Only explicit visibility flags justify replacing a reference with a placeholder.
const UNAVAILABLE_REFERENCE_SCHEMA = z
	.union([
		z.object({ status: z.literal('DELETED') }),
		z.object({ deleted: z.literal(true) }),
		z.object({ isPrivate: z.literal(true) }),
		z.object({ isSuppressed: z.literal(true) }),
	])
	.transform(() => ({ unavailable: true as const }))

const PUBLIC_REPOST_SOURCE_SCHEMA = z
	.object({
		type: z.literal('REPOST'),
		targetType: z.enum(['ORIGINAL_POST', 'REPOST']),
		target: z.union([UNAVAILABLE_REFERENCE_SCHEMA, PUBLIC_REFERENCE_SOURCE_SCHEMA]),
	})
	.superRefine(({ targetType, target }, context) => {
		if (!('unavailable' in target) && target.type !== targetType) {
			context.addIssue({ code: 'custom', path: ['target', 'type'], message: '引用动态类型不匹配' })
		}
	})

export function parsePublicRepostSnapshot(data: unknown, expectedId: string) {
	const snapshot = PUBLIC_REFERENCE_SOURCE_SCHEMA.parse(data)
	const { target } = PUBLIC_REPOST_SOURCE_SCHEMA.parse(data)

	if (snapshot.id !== expectedId) {
		throw new z.ZodError([{ code: 'custom', path: ['id'], message: '动态 ID 不匹配' }])
	}

	return { ...snapshot, type: 'REPOST' as const, target }
}

export type PublicRepostSnapshot = ReturnType<typeof parsePublicRepostSnapshot>
